import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, ButtonBase, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from 'views/components/ui-component/extended/Breadcrumbs';
import Sidebar from './Sidebar';
import Customization from '../Customization';
import SeperateItems from 'metadata/menu-items';
import { drawerWidth, RESPONSE_CODE, responseResult } from 'metadata/constant';

// assets
import { IconChevronRight } from '@tabler/icons-react';
import Header from './Header';
import { getDepartments } from '../../../../api/get/Department';
import { UpdateDepartmentsAction } from '../../../../store/action/actions';
import { getMyInfo } from '../../../../api/get/Auth';
import { UpdateUserInfoAction } from '../../../../store/action/userInfoAction';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    padding: '20px',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 140px)',
    ...(!open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            width: `100%`
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        [theme.breakpoints.up('md')]: {
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            width: `calc(100% - ${drawerWidth}px)`
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));
    // Handle left drawer
    const customization = useSelector((state) => state.customization);
    const dispatch = useDispatch();

    const navigation = SeperateItems();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuSelected, setMenuSelected] = useState('');
    const [isManualToggle, setIsManualToggle] = useState(false);

    const handleLeftDrawerToggle = () => {
        setIsManualToggle(true);
        setDrawerOpen(!drawerOpen);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    useEffect(() => {
        if (!matchDownSm) {
            setDrawerOpen(true);
        } else {
            setDrawerOpen(false);
        }
    }, [matchDownSm]);

    useEffect(() => {
        if (matchDownSm && !isManualToggle && menuSelected !== customization.isOpen[0]) {
            setDrawerOpen(false);
            setMenuSelected(customization.isOpen[0]);
        }
        setIsManualToggle(false);
    }, [customization.isOpen, matchDownSm, isManualToggle]);

    useEffect(() => {
        const fetchUserDetail = async () => {
            try {
                const response = await getMyInfo();
                if (responseResult(response.code) === RESPONSE_CODE.SUCCESS) {
                    const userData = response.data;
                    if (userData) {
                        const data = {
                            id: userData.id,
                            role: userData.role,
                            full_name: userData.full_name,
                            department: userData.department
                        };
                        dispatch(UpdateUserInfoAction({ userInfo: data }));
                    }
                }
            } catch (err) {}
        };

        const fetchDepartments = async () => {
            try {
                const response = await getDepartments();
                if (responseResult(response.code) === RESPONSE_CODE.SUCCESS) {
                    const departmentData = response.data;
                    if (departmentData) {
                        dispatch(UpdateDepartmentsAction({ departments: departmentData }));
                    }
                }
            } catch (err) {}
        };

        fetchUserDetail();
        fetchDepartments();
    }, []);

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.bgd.background,
                    transition: 'none',
                    width: '100%',
                    height: '100px'
                }}
            >
                <Toolbar sx={{ flexGrow: 1, justifyContent: 'space-between' }}>
                    <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                </Toolbar>
            </AppBar>
            <Sidebar drawerOpen={drawerOpen} handleDrawerClose={handleDrawerClose} />
            {/* main content */}
            <Box
                sx={{
                    width: '100%',
                    marginTop: '100px',
                    marginRight: '20px',
                    borderRadius: '12px',
                    backgroundColor: theme.palette.background.paper,
                    padding: '20px'
                }}
            >
                <Main theme={theme} open={!matchDownSm}>
                    <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign sx={{ mb: 2 }} />
                    <Outlet />
                </Main>
                <Customization />
            </Box>
        </Box>
    );
};

export default MainLayout;
